import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, MapPin, Globe, Instagram, Linkedin, Edit, Save, X, Crown, Star, Building, Target, Briefcase, User, ChevronDown, Camera, Search, Mail } from "lucide-react";
import { toast } from "sonner";
import { MatchButton } from "@/components/MatchContext";
import { PinButton } from "@/components/PinContext";

/* Bubble-grid selector. Selected chips appear at the top; below, the full options grid is shown
   as outlined bubbles users can click to add or remove. A search bar filters the bubble grid.
   New custom items get added to the options list once committed. */
const ChipSelector = ({ options, selected, onChange, label, placeholder, accentColor = "#FF2D87", textColor = "#FFFFFF", allowOther = false }) => {
  const [search, setSearch] = useState("");
  const [extraOptions, setExtraOptions] = useState([]);

  const allOptions = [...new Set([...(options || []), ...extraOptions, ...(selected || [])])];
  const unselected = allOptions.filter((o) => !selected.includes(o));
  const filtered = search
    ? unselected.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : unselected;

  const add = (item) => {
    if (!selected.includes(item)) onChange([...selected, item]);
    setSearch("");
  };
  const remove = (item) => onChange(selected.filter((s) => s !== item));

  const addCustom = () => {
    const v = search.trim();
    if (!v || selected.includes(v)) { setSearch(""); return; }
    if (!allOptions.includes(v)) setExtraOptions([...extraOptions, v]);
    onChange([...selected, v]);
    setSearch("");
  };

  return (
    <div>
      {/* Selected (filled) chips on top */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => remove(item)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors"
              style={{ backgroundColor: accentColor, color: textColor }}
              data-testid={`chip-selected-${item}`}
            >
              {item}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4A4A]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && allowOther && search.trim()) { e.preventDefault(); addCustom(); } }}
          className="w-full pl-10 pr-4 py-3 bg-white border border-[#E5E5E5] rounded-full text-sm focus:outline-none focus:border-[#FF2D87] transition-colors"
          placeholder={placeholder || `Search ${label.toLowerCase()}…`}
        />
      </div>

      {/* Outlined unselected bubbles — click to add */}
      <div className="flex flex-wrap gap-2">
        {filtered.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => add(item)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-colors hover:text-white"
            style={{ borderColor: accentColor, color: accentColor, backgroundColor: "transparent" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accentColor; e.currentTarget.style.color = textColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = accentColor; }}
            data-testid={`chip-option-${item}`}
          >
            + {item}
          </button>
        ))}
        {allowOther && search.trim() && !allOptions.some((o) => o.toLowerCase() === search.trim().toLowerCase()) && (
          <button
            type="button"
            onClick={addCustom}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border-2 border-dashed transition-colors"
            style={{ borderColor: accentColor, color: accentColor }}
            data-testid="chip-add-custom"
          >
            + Add "{search.trim()}"
          </button>
        )}
        {filtered.length === 0 && !search.trim() && (
          <p className="text-xs text-white/50">All options selected.</p>
        )}
      </div>
    </div>
  );
};

const Profile = () => {
  const { userId } = useParams();
  const { user: currentUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileOptions, setProfileOptions] = useState({ skills: [], industries: [], goals: [] });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Change password state
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwStatus, setPwStatus] = useState(null); // { kind: 'success' | 'error', message: string }

  // Public profile preview mode: "self" | "member" | "free"
  const [previewAs, setPreviewAs] = useState("self");

  const isOwnProfile = !userId || userId === currentUser?.user_id;
  const isPreviewing = isOwnProfile && !isEditing && previewAs !== "self";
  const hideContactInPreview = previewAs === "free";

  useEffect(() => {
    axios.get(`${API}/profile-options`).then((r) => setProfileOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOwnProfile && currentUser) {
      // Always fetch fresh profile data from API
      axios.get(`${API}/members/${currentUser.user_id}`)
        .then((r) => {
          setProfile(r.data);
          // Don't blow away in-progress edits if the user is mid-edit
          setEditForm((prev) => (isEditing ? prev : r.data));
        })
        .catch(() => { setProfile(currentUser); setEditForm((prev) => (isEditing ? prev : currentUser)); })
        .finally(() => setLoading(false));
    } else if (userId) {
      axios.get(`${API}/members/${userId}`)
        .then((r) => { setProfile(r.data); setEditForm(r.data); })
        .catch(() => navigate("/members"))
        .finally(() => setLoading(false));
    } else if (!currentUser) { navigate("/login"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentUser, isOwnProfile, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/members/me`, editForm);
      setProfile(response.data); setIsEditing(false);
      toast.success("Profile updated!");
      checkAuth();
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    if (newPassword.length < 8) {
      const msg = "New password must be at least 8 characters";
      setPwStatus({ kind: "error", message: msg });
      toast.error(msg);
      return;
    }
    if (newPassword !== confirmPassword) {
      const msg = "New password and confirmation do not match";
      setPwStatus({ kind: "error", message: msg });
      toast.error(msg);
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword || null,
        new_password: newPassword,
      });
      setPwStatus({
        kind: "success",
        message: "Password updated successfully. Use your NEW password the next time you log in.",
      });
      toast.success("Password updated — write it down!");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      // Keep the form open so the success banner stays visible until user dismisses
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update password. Your password was NOT changed.";
      setPwStatus({ kind: "error", message: msg });
      toast.error(msg);
    } finally { setPwSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/upload/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const newUrl = response.data.url;

      // If user is in edit mode, save the WHOLE form (preserving unsaved edits) along with the new picture.
      // Otherwise, just persist the picture.
      const payload = isEditing ? { ...editForm, picture: newUrl } : { picture: newUrl };
      const saved = await axios.put(`${API}/members/me`, payload);

      setProfile(saved.data);
      setEditForm(saved.data);
      toast.success(isEditing ? "Photo uploaded — your edits were saved too" : "Photo uploaded!");
      checkAuth();
    } catch { toast.error("Failed to upload photo"); }
    finally { setUploading(false); }
  };

  if (loading) return (<div className="min-h-screen bg-[#0A0A0A]"><Navbar /><div className="container-main py-20 text-center"><div className="spinner mx-auto" /></div></div>);

  if (!profile) return (<div className="min-h-screen bg-[#0A0A0A]"><Navbar /><div className="container-main py-20 text-center"><h1 className="text-3xl font-display font-bold text-white mb-4">Profile not found</h1><Link to="/members" className="text-[#FF2D87]">Back to Directory</Link></div><Footer /></div>);

  const pictureUrl = profile.picture?.startsWith("/api/") 
    ? `${process.env.REACT_APP_BACKEND_URL}${profile.picture}` 
    : profile.picture;

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <Navbar />
      <DashboardHeader />

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto">
          {!isOwnProfile && (
            <div className="flex items-center justify-between mb-8">
              <Link to="/members" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#FF2D87] transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Directory
              </Link>
              {currentUser && profile?.user_id && (
                <div className="flex items-center gap-2" data-testid="profile-actions">
                  <MatchButton targetUserId={profile.user_id} targetName={profile.name} showLabel />
                  <PinButton targetType="member" targetId={profile.user_id} className="w-9 h-9 rounded-full border-2 border-[#0A0A0A]" />
                </div>
              )}
            </div>
          )}

          {/* Public Profile Preview toggle — admins only, on own profile, not in edit mode */}
          {isOwnProfile && !isEditing && currentUser && currentUser.is_admin && (
            <div className="mb-6" data-testid="preview-toggle-bar">
              <div className="inline-flex items-center gap-1 p-1 bg-[#0A0A0A] rounded-full border border-[#FF2D87]/30">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 px-3">View as:</span>
                {[
                  { key: "self", label: "Me" },
                  { key: "member", label: "Paying Member" },
                  { key: "free", label: "Free Member" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setPreviewAs(opt.key)}
                    className={`text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-full transition-colors ${
                      previewAs === opt.key
                        ? "bg-[#FF2D87] text-white"
                        : "text-white/70 hover:text-white hover:bg-white/10"
                    }`}
                    data-testid={`preview-as-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {isPreviewing && (
                <p className="text-xs text-[#4A4A4A] mt-2">
                  You're previewing how your profile looks to a{" "}
                  <span className="font-bold text-[#FF2D87]">
                    {previewAs === "free" ? "Free Member (contact info only available to premium members)" : "Paying Member (full access)"}
                  </span>
                  . Editing is disabled in preview.
                </p>
              )}
            </div>
          )}

          {/* Incomplete profile banner — own profile only. Shown until ALL required fields are filled. */}
          {isOwnProfile && profile && (() => {
            const missing = [];
            if (!profile.picture) missing.push("a profile photo");
            if (!profile.bio) missing.push("a bio");
            if (!profile.title) missing.push("a title");
            if (!profile.location) missing.push("your location");
            if (!profile.skills || profile.skills.length === 0) missing.push("at least one skill");
            if (!profile.goals || profile.goals.length === 0) missing.push("at least one goal");
            if (missing.length === 0 || isPreviewing) return null;
            const list = missing.length === 1 ? missing[0] : missing.slice(0, -1).join(", ") + " and " + missing.slice(-1);
            return (
              <div className="mb-6 bg-[#FF2D87] text-white p-5 rounded-lg flex items-start gap-4 border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]" data-testid="profile-incomplete-banner">
                <div className="flex-shrink-0 mt-0.5">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm uppercase tracking-wider">Your profile is incomplete</h3>
                  <p className="text-sm mt-1 text-white/95">
                    You won't appear in the member directory until you add {list}. Every section must be filled — this applies to all members.
                  </p>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="mt-3 inline-flex items-center gap-2 bg-white text-[#FF2D87] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors"
                      data-testid="incomplete-banner-edit-btn"
                    >
                      Complete My Profile
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Profile Header */}
          <div className="bg-[#0A0A0A] rounded-t-lg p-8 md:p-10">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Avatar with upload */}
              <div className="relative flex-shrink-0 group">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#FF2D87] shadow-lg">
                  {pictureUrl ? (
                    <img src={pictureUrl} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#FF2D87] flex items-center justify-center text-white text-4xl font-bold">
                      {profile.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                {isOwnProfile && !isPreviewing && (
                  <>
                    <button onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-[#0A0A0A] hover:text-white transition-colors"
                      data-testid="upload-photo-btn">
                      {uploading ? <div className="spinner w-4 h-4" /> : <Camera className="w-4 h-4" />}
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </>
                )}
              </div>

              {/* Name + Info */}
              <div className="flex-1">
                <div className="flex gap-2 mb-2">
                  {profile.is_founder && (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF2D87] text-white px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Founder
                    </span>
                  )}
                  {profile.is_founding_member && (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-white text-[#FF2D87] px-3 py-1 rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Founding Member
                    </span>
                  )}
                </div>

                {isEditing ? (
                  <input type="text" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="text-3xl font-bold bg-transparent border-b-2 border-[#FF2D87]/50 focus:border-[#FF2D87] focus:outline-none w-full pb-1 text-[#FF2D87] placeholder:text-[#FF2D87]/50" placeholder="Your name" data-testid="edit-name-input" />
                ) : (
                  <h1 className="text-3xl font-display font-bold text-[#FF2D87]">{profile.name}</h1>
                )}

                {/* Title @ Company */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editForm.title || ""}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="text-base bg-transparent border-b border-white/50 focus:border-white focus:outline-none pb-1 w-52 text-white placeholder:text-white/50"
                        placeholder="Title"
                        data-testid="edit-title-input"
                      />
                      <span className="text-[#5170FF] font-bold text-lg">@</span>
                      <input type="text" value={editForm.company || ""} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                        className="text-base bg-transparent border-b border-white/50 focus:border-white focus:outline-none pb-1 w-48 text-white placeholder:text-white/50"
                        placeholder="Company" data-testid="edit-company-input" />
                    </>
                  ) : (
                    (profile.title || profile.company) && (
                      <span className="text-white">
                        {profile.title}
                        {profile.title && profile.company && <span className="text-[#5170FF] font-bold mx-1.5">@</span>}
                        {profile.company}
                      </span>
                    )
                  )}
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-2 mt-2">
                    <MapPin className="w-4 h-4 text-white/50" />
                    <input type="text" value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                      className="text-sm bg-transparent border-b border-white/50 focus:border-white focus:outline-none pb-1 w-48 text-white placeholder:text-white/50" placeholder="Location" data-testid="edit-location-input" />
                  </div>
                ) : profile.location ? (
                  <div className="flex items-center gap-1 text-sm text-[#5170FF] mt-2"><MapPin className="w-4 h-4" /> {profile.location}</div>
                ) : null}
              </div>

              {/* Edit Controls */}
              {isOwnProfile && !isPreviewing && (
                <div className="flex-shrink-0">
                  {isEditing ? (
                    <div className="flex gap-2">
                      <button onClick={() => { setIsEditing(false); setEditForm(profile); }} className="text-sm text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
                      <button onClick={handleSave} disabled={saving}
                        className="text-sm font-bold bg-[#0A0A0A] text-white px-5 py-2 rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors flex items-center gap-1" data-testid="save-profile-btn">
                        <Save className="w-4 h-4" /> {saving ? "..." : "Save"}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setIsEditing(true)}
                      className="text-sm font-bold bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full hover:bg-white hover:text-[#FF2D87] transition-colors flex items-center gap-1" data-testid="edit-profile-btn">
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Body - Black Card */}
          <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-b-lg overflow-hidden">
            {/* About */}
            <div className="p-8 md:px-10 border-b border-[#2A2A2A]">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#FFC2E8] mb-3">About</h2>
              {isEditing ? (
                <textarea value={editForm.bio || ""} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-[#2A2A2A] focus:border-[#FF2D87] focus:outline-none p-4 text-white rounded-lg resize-none min-h-[100px] transition-colors" placeholder="Tell us about yourself..." data-testid="edit-bio-input" />
              ) : (
                <p className="text-white leading-relaxed text-sm">{profile.bio || "No bio yet."}</p>
              )}
            </div>

            {/* Industry */}
            <div className="p-8 md:px-10 border-b border-[#2A2A2A]">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#FFC2E8] mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Industry
              </h2>
              {isEditing ? (
                <div>
                  <select value={editForm.industry || ""}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value, industry_other: e.target.value === "Other" ? editForm.industry_other : "" })}
                    className="w-full md:w-80 px-4 py-3 bg-white border-none rounded-lg text-sm text-[#0A0A0A] focus:outline-none" data-testid="edit-industry-select">
                    <option value="">Select your industry</option>
                    {profileOptions.industries.map((ind) => (<option key={ind} value={ind}>{ind}</option>))}
                  </select>
                  {editForm.industry === "Other" && (
                    <input type="text" value={editForm.industry_other || ""} onChange={(e) => setEditForm({ ...editForm, industry_other: e.target.value })}
                      className="mt-3 w-full md:w-80 px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white focus:border-[#FF2D87] focus:outline-none" placeholder="Specify your industry" data-testid="edit-industry-other-input" />
                  )}
                </div>
              ) : (
                <p className="text-white font-medium text-sm">{profile.industry === "Other" && profile.industry_other ? profile.industry_other : profile.industry || "Not specified"}</p>
              )}
            </div>

            {/* Skills */}
            <div className="p-8 md:px-10 border-b border-[#2A2A2A]">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#FFC2E8] mb-4">Skills & Expertise</h2>
              {isEditing ? (
                <ChipSelector options={profileOptions.skills} selected={editForm.skills || []}
                  onChange={(skills) => setEditForm({ ...editForm, skills })} label="Skills"
                  placeholder="Type to search skills or add your own..." accentColor="#FF2D87" allowOther={true} />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(profile.skills || []).map((s) => (
                    <span key={s} className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full bg-[#FF2D87] text-white">{s}</span>
                  ))}
                  {(!profile.skills || profile.skills.length === 0) && <p className="text-white text-sm">No skills added yet.</p>}
                </div>
              )}
            </div>

            {/* Goals */}
            <div className="p-8 md:px-10 border-b border-[#2A2A2A]">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#FFC2E8] mb-4 flex items-center gap-2">
                <Target className="w-4 h-4" /> Why I'm Here
              </h2>
              {isEditing ? (
                <ChipSelector options={profileOptions.goals} selected={editForm.goals || []}
                  onChange={(goals) => setEditForm({ ...editForm, goals })} label="Goals"
                  placeholder="Type to search goals..." accentColor="#FF2D87" textColor="#FFFFFF" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(profile.goals || []).map((g) => (
                    <span key={g} className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full bg-[#FF2D87] text-white">{g}</span>
                  ))}
                  {(!profile.goals || profile.goals.length === 0) && <p className="text-white text-sm">No goals specified yet.</p>}
                </div>
              )}
            </div>

            {/* Social Links */}
            <div className="p-8 md:px-10">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#FFC2E8] mb-4">Connect</h2>
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: Mail, field: "contact_email", placeholder: "you@example.com", testId: "edit-contact-email-input" },
                    { icon: Globe, field: "website", placeholder: "Website URL", testId: "edit-website-input" },
                    { icon: Instagram, field: "instagram", placeholder: "@username", testId: "edit-instagram-input" },
                    { icon: Linkedin, field: "linkedin", placeholder: "LinkedIn URL", testId: "edit-linkedin-input" },
                  ].map(({ icon: Icon, field, placeholder, testId }) => (
                    <div key={field} className="flex items-center gap-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-4 py-3">
                      <Icon className="w-5 h-5 text-[#4A4A4A] flex-shrink-0" />
                      <input type="text" value={editForm[field] || ""} onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                        className="bg-transparent focus:outline-none w-full text-sm text-white" placeholder={placeholder} data-testid={testId} />
                    </div>
                  ))}
                </div>
              ) : hideContactInPreview ? (
                <div className="flex items-center gap-3 bg-[#1A1A1A] border border-dashed border-[#FF2D87]/40 rounded-lg px-4 py-4" data-testid="locked-contact-preview">
                  <span className="text-2xl">🔒</span>
                  <div>
                    <p className="text-white text-sm font-bold">Contact info only available to premium members</p>
                    <p className="text-white/60 text-xs mt-0.5">Upgrade to a paid tier to unlock email, website, Instagram, and LinkedIn.</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3 flex-wrap">
                  {(() => {
                    const emailAddr = profile.contact_email || profile.display_email || profile.email;
                    const items = [
                      emailAddr && { icon: Mail, href: `mailto:${emailAddr}` },
                      profile.website && { icon: Globe, href: profile.website },
                      profile.instagram && { icon: Instagram, href: `https://instagram.com/${profile.instagram?.replace('@','')}` },
                      profile.linkedin && { icon: Linkedin, href: profile.linkedin },
                    ].filter(Boolean);
                    return items.map(({ icon: Icon, href }, i) => (
                      <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border-2 border-[#5170FF] flex items-center justify-center text-[#5170FF] hover:bg-[#5170FF] hover:text-white transition-all duration-200">
                        <Icon className="w-5 h-5" />
                      </a>
                    ));
                  })()}
                  {!profile.contact_email && !profile.display_email && !profile.email && !profile.website && !profile.instagram && !profile.linkedin && (
                    <p className="text-white text-sm">No contact info added yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Business directory CTA — own profile only */}
        {isOwnProfile && !isPreviewing && (
          <div className="bg-[#FF2D87] border-2 border-[#0A0A0A] rounded-2xl mt-8 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]" data-testid="business-cta-banner">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-white/80 mb-1">Female-Run Business Directory</p>
              <h3 className="text-white font-display text-2xl sm:text-3xl font-bold leading-tight">
                Do you have a business or brand?
              </h3>
              <p className="text-white/90 text-sm sm:text-base mt-1">Add it to our directory now.</p>
            </div>
            <Link
              to="/businesses"
              className="self-start sm:self-auto inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-bold rounded-full px-6 py-3 hover:bg-white hover:text-[#0A0A0A] transition-colors text-xs uppercase tracking-widest whitespace-nowrap"
              data-testid="business-cta-btn"
            >
              Add my business
            </Link>
          </div>
        )}

        {/* Account Settings — only for own profile (hidden in preview mode) */}
        {isOwnProfile && !isPreviewing && (
          <div className="bg-[#0A0A0A] border border-[#FF2D87]/30 rounded-2xl mt-8 p-8" data-testid="account-settings">
            <h2 className="text-white font-display text-2xl font-bold mb-2">ACCOUNT SETTINGS</h2>
            <p className="text-white/60 text-sm mb-6">Manage your login and security.</p>

            {!pwOpen ? (
              <button
                onClick={() => { setPwOpen(true); setPwStatus(null); }}
                className="px-6 py-3 bg-[#FF2D87] hover:bg-[#e0226e] text-white font-bold rounded-full transition-colors"
                data-testid="open-change-password-btn"
              >
                Change Password
              </button>
            ) : (
              <form onSubmit={handleChangePassword} className="max-w-md space-y-4" data-testid="change-password-form">
                {pwStatus && (
                  <div
                    className={`border-2 p-4 rounded-lg ${
                      pwStatus.kind === "success"
                        ? "bg-[#FF2D87]/15 border-[#FF2D87] text-white"
                        : "bg-red-500/15 border-red-500 text-white"
                    }`}
                    data-testid={`pw-status-${pwStatus.kind}`}
                  >
                    <p className="text-xs font-bold uppercase tracking-widest mb-1">
                      {pwStatus.kind === "success" ? "✓ Password Changed" : "✗ Password NOT Changed"}
                    </p>
                    <p className="text-sm">{pwStatus.message}</p>
                  </div>
                )}
                <div>
                  <label className="block text-white/80 text-xs uppercase tracking-wider font-bold mb-2">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Leave blank if you've never set one"
                    className="w-full bg-[#1A1A1A] border border-[#2A2A2A] focus:border-[#FF2D87] outline-none text-white px-4 py-3 rounded-lg"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-xs uppercase tracking-wider font-bold mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    className="w-full bg-[#1A1A1A] border border-[#2A2A2A] focus:border-[#FF2D87] outline-none text-white px-4 py-3 rounded-lg"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-xs uppercase tracking-wider font-bold mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-[#1A1A1A] border border-[#2A2A2A] focus:border-[#FF2D87] outline-none text-white px-4 py-3 rounded-lg"
                    data-testid="confirm-password-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={pwSaving}
                    className="px-6 py-3 bg-[#FF2D87] hover:bg-[#e0226e] disabled:opacity-50 text-white font-bold rounded-full transition-colors"
                    data-testid="save-password-btn"
                  >
                    {pwSaving ? "Saving…" : "Update Password"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPwOpen(false); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setPwStatus(null); }}
                    className="px-6 py-3 border border-white/30 text-white hover:bg-white/10 rounded-full transition-colors"
                    data-testid="cancel-password-btn"
                  >
                    {pwStatus?.kind === "success" ? "Close" : "Cancel"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
